import { Button } from "@ews-admin/global-design-system";
import React from "react";
import { Control, FieldErrors, UseFormSetValue } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Specialty } from "../../../types/specialty.definition";
import { Staff } from "../../../types/staff.definition";
import {
  DoctorSpecificFields,
  NotificationPreferences,
  PersonalInfoSection,
  RoleSelector,
} from "./components";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StaffFormData = any;

interface DoctorFormProps {
  control: Control<StaffFormData>;
  setValue: UseFormSetValue<StaffFormData>;
  errors: FieldErrors<StaffFormData>;
  specialties: Specialty[];
  selectedSpecialties: Array<{ id: string; code: string; label: string }>;
  editingStaff: Staff | null;
  isSubmitting: boolean;
  isSubmitDisabled: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
  isDoctorLimitReached?: boolean;
  isSecretaryLimitReached?: boolean;
  onUpgradeLicence?: () => void;
}

export const DoctorForm: React.FC<DoctorFormProps> = ({
  control,
  setValue,
  errors,
  specialties,
  selectedSpecialties,
  editingStaff,
  isSubmitting,
  isSubmitDisabled,
  onSubmit,
  onCancel,
  isDoctorLimitReached,
  isSecretaryLimitReached,
  onUpgradeLicence,
}) => {
  const { t } = useTranslation();
  return (
    <form onSubmit={onSubmit} className="space-y-6">
      {/* Role Selection */}

      {!editingStaff && (
        <RoleSelector
          control={control}
          setValue={setValue}
          disabled={!!editingStaff}
          isDoctorLimitReached={isDoctorLimitReached}
          isSecretaryLimitReached={isSecretaryLimitReached}
          onUpgradeLicence={onUpgradeLicence}
        />
      )}

      {/* Personal Information */}
      <PersonalInfoSection
        control={control}
        setValue={setValue}
        errors={errors}
        emailDisabled={!!editingStaff}
        phoneDisabled={!!editingStaff}
        dateOfBirthRequired={!editingStaff}
      />

      {/* Doctor-Specific Fields */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <DoctorSpecificFields
          control={control}
          errors={errors}
          specialties={specialties}
          selectedSpecialties={selectedSpecialties}
          isEditing={!!editingStaff}
        />
      </div>

      {/* Notification Preferences (MED-1653: the doctor form had none, unlike the secretary's) */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <NotificationPreferences
          control={control}
          setValue={setValue}
          errors={errors}
        />
      </div>

      {/* Form Actions */}
      <div className="flex gap-3 justify-end pt-6 border-t border-neutral-200">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting}
        >
          {t("common.cancel")}
        </Button>
        <Button
          type="submit"
          loading={isSubmitting}
          disabled={isSubmitDisabled || isSubmitting}
        >
          {editingStaff
            ? t("staff.manage.form.updateStaff")
            : t("staff.manage.form.addStaff")}
        </Button>
      </div>
    </form>
  );
};
